<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router/composables";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";
import { ButtonSeverity, isNullish, TuButton } from "@tinyurldev/tu-ui-vue";

import useTranslation from "@Composables/useTranslation";
import OrgSettingsLayout from "@Modules/teams/Views/OrgSettingsLayout.vue";
import { XTeamsStore } from "@Modules/teams/Store/XTeamsStore.ts";
import type { Organization } from "@Modules/teams/Data/Models/OrganizationModel.ts";
import type { ApiToken, ApiTokenPayload } from "@Modules/teams/Data/Models/ApiToken";
import SettingsTopBar from "@Modules/teams/components/SettingsTopBar.vue";
import OrgApiTokensTable from "@Modules/teams/components/OrgApiTokensTable.vue";
import EditApiTokenModal from "@Modules/teams/Modals/EditApiTokenModal.vue";
import DeleteApiTokenModal from "@Modules/teams/Modals/DeleteApiTokenModal.vue";
import CopyTokenModal from "@Modules/teams/Modals/CopyTokenModal.vue";
import ApiTokensApi from "@Modules/teams/Api/ApiTokensApi";
import XOverlay from "@Modules/shared/store/XOverlay";
import showModal from "@Resources/js/helpers/functions/showModal.js";
import OrgSettingsContent from "@Modules/teams/components/OrgSettingsContent.vue";
import CreateEditTokenForm from "@Modules/teams/components/CreateEditTokenForm.vue";
import { logError } from "@Resources/js/helpers/logger";
import { Team } from "@Modules/teams/Data/Models/TeamModel";
import OrgDropdown, { DropdownItem } from "@Modules/teams/components/OrgDropdown.vue";

//
// Setup
const { __ } = useTranslation();
const route = useRoute();
const teams = XTeamsStore();
const overlay = XOverlay();
const apiTokensApi = ApiTokensApi();
const breakpoints = useBreakpoints(breakpointsBootstrapV5);
const isMobileView = breakpoints.smallerOrEqual("xl");
const selectedTeam = ref<Team | null | undefined>();

//
// Computed
const isDropdownVisible = computed(() => isNullish(currentOrganization.value?.defaultTeam()));

/** The slug of the current organization and team */
const orgSlug = computed<string>(() => route?.params?.org || "");

/** The currently loaded organization */
const currentOrganization = computed<Organization | null>(() =>
    teams.getOrganizationBySlug(orgSlug.value),
);

const tokens = computed(() => apiTokensApi.TeamTokens.value(selectedTeam.value?.slug ?? null));

const selectableTeams = computed<DropdownItem[] | null>(() => {
  const teams = currentOrganization.value?.publicTeams();

  if (isNullish(teams)) {
    return null;
  }

  return teams.map((team) => ({
    id: team.slug,
    label: team.name,
    onClick: () => selectedTeam.value = team,
  }));
});

//
// Methods

async function fetchTokensForTeam(teamSlug: string): Promise<void> {
  if (!currentOrganization.value) {
    return;
  }

  await apiTokensApi.fetchTeamTokens({
    orgSlug: currentOrganization.value.slug,
    teamSlug,
  });
}

async function createToken(payload: ApiTokenPayload): Promise<ApiToken | null> {
  const team = selectedTeam.value;

  if (!currentOrganization.value || isNullish(team)) {
    return null;
  }

  const orgSlugVal = currentOrganization.value.slug;

  return apiTokensApi.createToken({
    orgSlug: orgSlugVal,
    teamSlug: team.slug,
    payload,
  });
}

function openCopyModal(token: ApiToken): void {
  showModal(CopyTokenModal, { token });
}

function openEditModal(token: ApiToken): void {
  const team = selectedTeam.value;

  if (!currentOrganization.value || isNullish(team)) {
    return;
  }

  const orgSlugVal = currentOrganization.value.slug;

  apiTokensApi.clearErrors();
  overlay.showModal(EditApiTokenModal, {
    token,
    submit: async (payload: ApiTokenPayload) => {
      return apiTokensApi.updateToken({
        orgSlug: orgSlugVal,
        teamSlug: team.slug,
        tokenId: token.id,
        payload,
      });
    },
    errors: apiTokensApi.errors,
    loading: apiTokensApi.loading,
    save: () => fetchTokensForTeam(team.slug),
    clearErrors: apiTokensApi.clearErrors,
  });
}

function openDeleteModal(token: ApiToken): void {
  const team = selectedTeam.value;

  if (!currentOrganization.value || isNullish(team)) {
    return;
  }

  const orgSlugVal = currentOrganization.value.slug;

    overlay.showModal(DeleteApiTokenModal, {
        token,
        deleteToken: () => {
            return apiTokensApi.deleteToken({
                orgSlug: orgSlugVal,
                teamSlug: team.slug,
                tokenId: token.id,
            });
        },
    });
};

onMounted(() => {
  const defaultTeam = currentOrganization.value?.defaultTeam();

  if (isNullish(defaultTeam)) {
    return;
  }

  selectedTeam.value = defaultTeam;

  fetchTokensForTeam(defaultTeam.slug)
    .catch(() => {
      logError("[fetchTokensForTeam]: Cannot fetch tokens from default team.");
    });
});
</script>

<template>
  <OrgSettingsLayout
    v-if="currentOrganization"
    class="api-token-settings"
    :organization="currentOrganization"
    :can="currentOrganization.can('use_api')"
  >
    <div class="api-token-settings__content">
      <SettingsTopBar :title="__('organization_api_settings.top_bar_title')">
        <div class="api-token-settings__actions">
          <OrgDropdown
            v-if="isDropdownVisible && selectableTeams"
            class="_team_dropdown"
            :selected="'Select a Team'"
            :items="selectableTeams"
          />
          <TuButton
            as="router-link"
            class="_dev_docs"
            :label="__('organization_api_settings.developer_docs')"
            :severity="ButtonSeverity.SECONDARY"
            :to="{ name: 'resources.dev' }"
            :is-block="isMobileView"
          />
        </div>
      </SettingsTopBar>

      <OrgSettingsContent
        :title="__('organization_api_settings.create_tokens_title')"
        :description="__('organization_api_settings.create_tokens_description')"
      >
        <CreateEditTokenForm
          class="_create_token"
          :submit="createToken"
          :loading="apiTokensApi.loading.value"
          :errors="apiTokensApi.errors.value"
          @created="openCopyModal"
        />
      </OrgSettingsContent>

      <OrgSettingsContent
        v-if="selectedTeam"
        :title="__('organization_api_settings.manage_tokens_title')"
        :description="__('organization_api_settings.manage_tokens_description')"
      >
        <OrgApiTokensTable
          :team="selectedTeam"
          :tokens="tokens"
          :loading="apiTokensApi.loading.value"
          :disabled="false"
          :edit-token="(token) => openEditModal(token)"
          :delete-token="(token) => openDeleteModal(token)"
        />
        <p class="api-token-settings__token-counter">
          {{
            tokens.length === 1
              ? __("token_with_count_one", { count: tokens.length })
              : __("token_with_count_other", { count: tokens.length })
          }}
        </p>
      </OrgSettingsContent>

      <div
        v-else
        class="_no_access text-center py-4"
      >
        <i class="fa-solid fa-circle-exclamation" />
        {{ __('organization_api_settings.no_access') }}
      </div>
    </div>
  </OrgSettingsLayout>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;

.api-token-settings {
  &__content {
    display: flex;
    flex-flow: column;
    gap: spacing.$s;
  }

  &__actions {
    display: flex;
    gap: spacing.$xxs;
    align-items: center;
  }

  &__token-counter {
    font-size: typography.$base-font-size;
    font-weight: typography.$font-weight-bold;
    line-height: typography.$base-line-height;
  }
}
</style>
